import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toast from 'shared/common/toast';
import api from 'api/client';
import { userStore } from 'store/UserStore';
import decodeCognitoToken from './services/cognitoVerify';

/**
 * Entry point for eBullion's "Buy Gold/Silver Bar" hand-off. eBullion redirects
 * here with the customer's Cognito ID token and the product code as plain query
 * params: /handle-redirect?token=<idToken>&product=<itemCode>. There is no
 * encryption on this param — the token itself is only decoded here for a
 * structural/expiry sanity check, then stored and forwarded as a Bearer token
 * exactly like every other auth flow in this app, so an invalid/forged token is
 * rejected on first use by the real backend.
 */
const HandleRedirect = () => {
  const history = useHistory();
  const location = useLocation();
  const setLoginData = userStore((state: any) => state.setLoginData);
  const [failed, setFailed] = useState(false);
  const [failReason, setFailReason] = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    processRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processRedirect = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const product = params.get('product');

      // A session already exists — either the customer landed back here via
      // browser back/forward (eBullion's page is still in history behind
      // this one) or eBullion redirected again after the session was already
      // established. Either way, don't re-process: just continue on with the
      // existing session instead of touching tokens or restarting anything.
      if (!token && api.getToken()) {
        if (product) localStorage.setItem('Product', product);
        history.replace('/customers');
        return;
      }

      if (!token) throw new Error('Missing token param');

      decodeCognitoToken(token, 'id');

      api.setToken(token);
      setLoginData({
        tokens: { idToken: token, accessToken: null },
        userData: {},
      });

      if (product) {
        // Read by Stepper (pages/buy-coins-bars/index.tsx) on mount, which
        // fetches this product and jumps straight to step 2. CustomerPage's
        // post-login redirect (redirectCustomer) checks for this same key to
        // send the customer to /buy-coins-bars instead of /dashboard, since
        // customer/customerId must be hydrated there first.
        localStorage.setItem('Product', product);
      }

      // replace, not push: this route must not become a back-button stop —
      // going back should skip over it (and over eBullion's OTP page) rather
      // than reprocessing the hand-off or resurfacing a stale mobile-entry form.
      history.replace('/customers');
    } catch (e) {
      // Never log the raw token.
      const message = e instanceof Error ? e.message : 'Unknown error';
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'production') console.error('[handle-redirect]', message);
      setFailReason(message);
      setFailed(true);
    }
  };

  useEffect(() => {
    if (!failed) return;
    toast.error(
      process.env.NODE_ENV !== 'production'
        ? `Could not verify your session: ${failReason}`
        : 'We could not verify your session. Please sign in again.',
    );
    const timer = setTimeout(() => history.replace('/signin'), 2500);
    return () => clearTimeout(timer);
  }, [failed, failReason, history]);

  return (
    <div>
      <Toast />
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        {!failed && (
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandleRedirect;
